/**
 * useFlightPlan — manages flight plan CRUD, route computation, and NavLog generation.
 */
import { useState, useCallback, useRef, useEffect } from 'react';
import {
  RouteEngine,
  WaypointResolver,
  NavLogGenerator,
  FormatExporter,
  FormatParser,
  type FlightPlan,
  type FlightPlanParams,
  type FlightPlanSummary,
  type AircraftPerformance,
  type ExportFormat,
  type RouteWaypoint,
  DEFAULT_PERFORMANCE,
} from '@efb/shared-core';
import { RestWaypointDataProvider } from '../adapters/RestWaypointDataProvider';
import { IndexedDBStorageAdapter } from '../adapters/IndexedDBStorageAdapter';
import { v4 as uuidv4 } from 'uuid';

const PLANS_PREFIX = 'flight-plan:';

export function buildImportedPlan(
  partial: Partial<FlightPlan>,
  routeEngine = new RouteEngine(),
): FlightPlan {
  const now = Date.now();
  const cruiseAltitude = partial.cruiseAltitude ?? 5500;
  const trueAirspeed = partial.trueAirspeed ?? DEFAULT_PERFORMANCE.cruiseTas;
  const rawWaypoints = partial.waypoints?.length
    ? partial.waypoints
    : [partial.departure, partial.destination].filter(Boolean);

  if (rawWaypoints.length < 2) {
    throw new Error('Imported flight plan must contain at least two waypoints');
  }

  const waypoints: RouteWaypoint[] = rawWaypoints.map((waypoint, sequenceIndex) => {
    const plannedAltitude = 'plannedAltitude' in waypoint!
      && typeof waypoint!.plannedAltitude === 'number'
      ? waypoint!.plannedAltitude
      : cruiseAltitude;
    return { ...waypoint!, sequenceIndex, plannedAltitude };
  });
  const route = routeEngine.buildRoute(waypoints, trueAirspeed);
  const departure = partial.departure ?? route.waypoints[0];
  const destination = partial.destination ?? route.waypoints[route.waypoints.length - 1];

  return {
    id: partial.id ?? uuidv4(),
    name: partial.name ?? `${departure.identifier}-${destination.identifier}`,
    status: partial.status ?? 'draft',
    flightRules: partial.flightRules ?? 'VFR',
    departure,
    destination,
    waypoints: route.waypoints,
    legs: route.legs,
    cruiseAltitude,
    trueAirspeed,
    aircraftProfileId: partial.aircraftProfileId ?? null,
    totalDistanceNm: route.totalDistanceNm,
    totalTimeMinutes: route.totalTimeMinutes,
    totalFuelGallons: partial.totalFuelGallons ?? null,
    filingReference: partial.filingReference ?? null,
    filingStatus: partial.filingStatus ?? 'not_filed',
    createdAt: partial.createdAt ?? now,
    updatedAt: now,
    departureTime: partial.departureTime ?? null,
    navLog: partial.navLog ?? null,
    weatherAssessment: partial.weatherAssessment ?? null,
    conflicts: partial.conflicts ?? [],
    clearanceResult: partial.clearanceResult ?? null,
  };
}

export function useFlightPlanState() {
  const [currentPlan, setCurrentPlan] = useState<FlightPlan | null>(null);
  const [planList, setPlanList] = useState<FlightPlanSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const storageRef = useRef(new IndexedDBStorageAdapter());
  const providerRef = useRef(new RestWaypointDataProvider());
  const routeEngineRef = useRef(new RouteEngine());
  const resolverRef = useRef(new WaypointResolver(providerRef.current));
  const navLogGenRef = useRef(new NavLogGenerator(routeEngineRef.current));
  const exporterRef = useRef(new FormatExporter());
  const parserRef = useRef(new FormatParser());

  const refreshPlanList = useCallback(async () => {
    const keys = await storageRef.current.keys(PLANS_PREFIX);
    const summaries: FlightPlanSummary[] = [];
    for (const key of keys) {
      const plan = await storageRef.current.get<FlightPlan>(key);
      if (plan) {
        summaries.push({
          id: plan.id, name: plan.name,
          departure: plan.departure.identifier, destination: plan.destination.identifier,
          flightRules: plan.flightRules, status: plan.status, filingStatus: plan.filingStatus,
          totalDistanceNm: plan.totalDistanceNm, createdAt: plan.createdAt, updatedAt: plan.updatedAt,
        });
      }
    }
    summaries.sort((a, b) => b.updatedAt - a.updatedAt);
    setPlanList(summaries);
  }, []);

  useEffect(() => { refreshPlanList(); }, [refreshPlanList]);

  const createPlan = useCallback(async (params: FlightPlanParams) => {
    setLoading(true); setError(null);
    try {
      const departure = await resolverRef.current.resolveWaypointOrThrow(params.departure);
      const destination = await resolverRef.current.resolveWaypointOrThrow(params.destination);
      const intermediates = params.intermediates
        ? await Promise.all(params.intermediates.map(id => resolverRef.current.resolveWaypointOrThrow(id)))
        : [];
      const route = await routeEngineRef.current.generateRoute({
        departure, destination, intermediates,
        flightRules: params.flightRules, cruiseAltitude: params.cruiseAltitude,
        trueAirspeed: params.trueAirspeed ?? DEFAULT_PERFORMANCE.cruiseTas,
      });

      const plan: FlightPlan = {
        id: uuidv4(), name: params.name || `${departure.identifier}-${destination.identifier}`,
        status: 'draft', flightRules: params.flightRules,
        departure, destination, waypoints: route.waypoints, legs: route.legs,
        cruiseAltitude: params.cruiseAltitude, trueAirspeed: params.trueAirspeed ?? DEFAULT_PERFORMANCE.cruiseTas,
        aircraftProfileId: params.aircraftProfileId ?? null,
        totalDistanceNm: route.totalDistanceNm, totalTimeMinutes: route.totalTimeMinutes,
        totalFuelGallons: null, filingReference: null, filingStatus: 'not_filed',
        createdAt: Date.now(), updatedAt: Date.now(), departureTime: params.departureTime ?? null,
        navLog: null, weatherAssessment: null, conflicts: [], clearanceResult: null,
      };

      await storageRef.current.set(`${PLANS_PREFIX}${plan.id}`, plan);
      setCurrentPlan(plan);
      await refreshPlanList();
      return plan;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to create plan';
      setError(msg); throw e;
    } finally { setLoading(false); }
  }, [refreshPlanList]);

  const generateNavLog = useCallback(async (
    planId: string, aircraft?: AircraftPerformance,
  ) => {
    const plan = currentPlan?.id === planId ? currentPlan
      : await storageRef.current.get<FlightPlan>(`${PLANS_PREFIX}${planId}`);
    if (!plan) throw new Error('Plan not found');

    const ac = aircraft ?? DEFAULT_PERFORMANCE;
    const route = { waypoints: plan.waypoints, legs: plan.legs, totalDistanceNm: plan.totalDistanceNm, totalTimeMinutes: plan.totalTimeMinutes };
    const navLog = await navLogGenRef.current.generateNavLog(route, ac);
    const updated = { ...plan, navLog, totalFuelGallons: navLog.totalFuelGallons, updatedAt: Date.now() };
    await storageRef.current.set(`${PLANS_PREFIX}${planId}`, updated);
    setCurrentPlan(updated);
    return navLog;
  }, [currentPlan]);

  const exportPlan = useCallback((plan: FlightPlan, format: ExportFormat) => {
    return exporterRef.current.export(plan, format);
  }, []);

  const saveImportedPlan = useCallback(async (partial: Partial<FlightPlan>) => {
    setLoading(true); setError(null);
    try {
      const plan = buildImportedPlan(partial, routeEngineRef.current);
      await storageRef.current.set(`${PLANS_PREFIX}${plan.id}`, plan);
      setCurrentPlan(plan);
      await refreshPlanList();
      return plan;
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to import plan';
      setError(msg); throw e;
    } finally { setLoading(false); }
  }, [refreshPlanList]);

  const importPlan = useCallback(async (data: string) => {
    const format = parserRef.current.detectFormat(data);
    if (!format) throw new Error('Unrecognized flight plan format');
    return saveImportedPlan(parserRef.current.parse(data, format));
  }, [saveImportedPlan]);

  const loadPlan = useCallback(async (planId: string) => {
    const plan = await storageRef.current.get<FlightPlan>(`${PLANS_PREFIX}${planId}`);
    setCurrentPlan(plan);
    return plan;
  }, []);

  const deletePlan = useCallback(async (planId: string) => {
    await storageRef.current.remove(`${PLANS_PREFIX}${planId}`);
    if (currentPlan?.id === planId) setCurrentPlan(null);
    await refreshPlanList();
  }, [currentPlan, refreshPlanList]);

  const clearCurrentPlan = useCallback(() => setCurrentPlan(null), []);

  return {
    currentPlan, planList, loading, error,
    createPlan, loadPlan, deletePlan, generateNavLog, exportPlan, importPlan,
    saveImportedPlan, clearCurrentPlan,
    refreshPlanList,
  };
}
